// GitHub data for the PR list and the review (SPEC §6 "Data").
import type { GitHub } from '../github/client';
import { normalizeText } from '../workcopy/normalize';

type Repo = { owner: string; name: string };
const isMarkdown = (p: string) => p.toLowerCase().endsWith('.md');

// ---------- PR list ----------

export type PrStatus = 'draft' | 'review-requested' | 'changes-requested' | 'approved' | 'open';

export type PrSummary = {
  number: number;
  title: string;
  url: string;
  author: string;
  isDraft: boolean;
  headRef: string;
  baseRef: string;
  updatedAt: string;
  markdownFiles: number;
  comments: number;
  requestedReviewers: string[];
  approvedBy: string[];
  changesRequestedBy: string[];
  /** The single most useful status for the viewer. */
  status: PrStatus;
};

const PR_LIST = `query($owner: String!, $name: String!) {
  viewer { login }
  repository(owner: $owner, name: $name) {
    pullRequests(states: OPEN, first: 50, orderBy: { field: UPDATED_AT, direction: DESC }) {
      nodes {
        number title url isDraft updatedAt headRefName baseRefName
        author { login }
        reviewRequests(first: 20) { nodes { requestedReviewer { __typename ... on User { login } } } }
        latestOpinionatedReviews(first: 20) { nodes { state author { login } } }
        files(first: 100) { nodes { path } }
        comments { totalCount }
        reviewThreads { totalCount }
      }
    }
  }
}`;

type PrListData = {
  viewer: { login: string };
  repository: { pullRequests: { nodes: {
    number: number; title: string; url: string; isDraft: boolean; updatedAt: string; headRefName: string; baseRefName: string;
    author: { login: string } | null;
    reviewRequests: { nodes: { requestedReviewer: { __typename: string; login?: string } | null }[] };
    latestOpinionatedReviews: { nodes: { state: string; author: { login: string } | null }[] };
    files: { nodes: { path: string }[] } | null;
    comments: { totalCount: number };
    reviewThreads: { totalCount: number };
  }[] } } | null;
};

export async function listPullRequests(gh: GitHub, repo: Repo): Promise<{ viewer: string; prs: PrSummary[] }> {
  const d = await gh.graphql<PrListData>(PR_LIST, { owner: repo.owner, name: repo.name }, ['repository.pullRequests']);
  const viewer = d.viewer.login;
  const prs = d.repository!.pullRequests.nodes
    .map((p): PrSummary => {
      const requested = p.reviewRequests.nodes.flatMap((r) => (r.requestedReviewer?.login ? [r.requestedReviewer.login] : []));
      const approvedBy = p.latestOpinionatedReviews.nodes.filter((r) => r.state === 'APPROVED').map((r) => r.author?.login ?? 'ghost');
      const changesBy = p.latestOpinionatedReviews.nodes.filter((r) => r.state === 'CHANGES_REQUESTED').map((r) => r.author?.login ?? 'ghost');
      const status: PrStatus = p.isDraft ? 'draft'
        : requested.includes(viewer) ? 'review-requested'
        : changesBy.length ? 'changes-requested'
        : approvedBy.length ? 'approved'
        : 'open';
      return {
        number: p.number, title: p.title, url: p.url, author: p.author?.login ?? 'ghost', isDraft: p.isDraft,
        headRef: p.headRefName, baseRef: p.baseRefName, updatedAt: p.updatedAt,
        markdownFiles: (p.files?.nodes ?? []).filter((f) => isMarkdown(f.path)).length,
        comments: p.comments.totalCount + p.reviewThreads.totalCount,
        requestedReviewers: requested, approvedBy, changesRequestedBy: changesBy, status,
      };
    })
    .filter((p) => p.markdownFiles > 0);
  return { viewer, prs };
}

export type PrFilter = 'needs-review' | 'yours' | 'all';
export function filterPrs(prs: PrSummary[], viewer: string, f: PrFilter): PrSummary[] {
  if (f === 'needs-review') return prs.filter((p) => p.requestedReviewers.includes(viewer));
  if (f === 'yours') return prs.filter((p) => p.author === viewer);
  return prs;
}

// ---------- Review ----------

export type ThreadComment = {
  id: string; author: string; body: string; createdAt: string; url: string; originalCommit: string | null;
  /** Part of a review that hasn't been submitted yet (only the author sees these). */
  pending: boolean;
};
export type ReviewThread = {
  id: string;
  path: string;
  isOutdated: boolean;
  isResolved: boolean;
  subjectType: 'LINE' | 'FILE';
  diffSide: 'LEFT' | 'RIGHT';
  line: number | null;
  startLine: number | null;
  originalLine: number | null;
  comments: ThreadComment[];
  /** More comments exist than were loaded (GitHub limit per request). */
  commentsTruncated: boolean;
};

export type PrFile = {
  path: string;
  previousPath: string | null;
  status: 'added' | 'removed' | 'modified' | 'renamed' | 'copied' | 'changed' | 'unchanged';
  additions: number;
  deletions: number;
  /** GitHub's patch, kept only for its hunk headers (M4); may be missing. */
  patch: string | null;
};

export type PrDetails = {
  id: string; number: number; title: string; url: string; author: string; isDraft: boolean;
  headRef: string; baseRef: string; headSha: string; mergeBase: string;
  /** Signed-in user. */
  viewer: string;
  /** The viewer's pending (unsubmitted) review on this PR, to resume (SPEC §6). */
  pendingReviewId: string | null;
  commits: number; comments: number;
  files: PrFile[];
  threads: ReviewThread[];
  /** More review threads exist than were loaded. Never drop comments silently: the UI says so. */
  threadsTruncated: boolean;
};

const PR_DETAILS = `query($owner: String!, $name: String!, $number: Int!) {
  viewer { login }
  repository(owner: $owner, name: $name) {
    pullRequest(number: $number) {
      id number title url isDraft headRefName baseRefName headRefOid baseRefOid
      reviews(states: PENDING, first: 5) { nodes { id viewerDidAuthor } }
      author { login }
      commits { totalCount }
      comments { totalCount }
      reviewThreads(first: 100) {
        totalCount
        pageInfo { hasNextPage }
        nodes {
          id path isOutdated isResolved subjectType diffSide line startLine originalLine
          comments(first: 50) { totalCount nodes { id body createdAt url state author { login } originalCommit { oid } } }
        }
      }
    }
  }
}`;

type PrDetailsData = { viewer?: { login: string }; repository: { pullRequest: {
  id?: string; reviews?: { nodes: { id: string; viewerDidAuthor: boolean }[] };
  number: number; title: string; url: string; isDraft: boolean; headRefName: string; baseRefName: string; headRefOid: string; baseRefOid: string;
  author: { login: string } | null; commits: { totalCount: number }; comments: { totalCount: number };
  reviewThreads: { totalCount?: number; pageInfo?: { hasNextPage: boolean }; nodes: {
    id: string; path: string; isOutdated: boolean; isResolved: boolean; subjectType: 'LINE' | 'FILE'; diffSide: 'LEFT' | 'RIGHT';
    line: number | null; startLine: number | null; originalLine: number | null;
    comments: { totalCount?: number; nodes: { id: string; body: string; createdAt: string; url: string; state?: string; author: { login: string } | null; originalCommit: { oid: string } | null }[] };
  }[] };
} | null } | null };

export class NotFoundError extends Error {}

export async function loadPullRequest(gh: GitHub, repo: Repo, number: number): Promise<PrDetails> {
  const d = await gh.graphql<PrDetailsData>(PR_DETAILS, { owner: repo.owner, name: repo.name, number }, ['repository.pullRequest']);
  const pr = d.repository!.pullRequest!;
  const R = `/repos/${encodeURIComponent(repo.owner)}/${encodeURIComponent(repo.name)}`;
  // Base = merge base (SPEC §6), not the tip of the base branch.
  const [compare, files] = await Promise.all([
    gh.rest<{ merge_base_commit: { sha: string } }>(`${R}/compare/${pr.baseRefOid}...${pr.headRefOid}`),
    gh.restPaged<{ filename: string; previous_filename?: string; status: PrFile['status']; additions: number; deletions: number; patch?: string }>(`${R}/pulls/${number}/files`),
  ]);
  if (!compare) throw new NotFoundError(`Couldn't compare ${pr.baseRefName}...${pr.headRefName}`);
  return {
    id: pr.id ?? '', viewer: d.viewer?.login ?? '',
    pendingReviewId: pr.reviews?.nodes.find((r) => r.viewerDidAuthor)?.id ?? null,
    number: pr.number, title: pr.title, url: pr.url, author: pr.author?.login ?? 'ghost', isDraft: pr.isDraft,
    headRef: pr.headRefName, baseRef: pr.baseRefName, headSha: pr.headRefOid, mergeBase: compare.merge_base_commit.sha,
    commits: pr.commits.totalCount, comments: pr.comments.totalCount,
    files: files.filter((f) => isMarkdown(f.filename)).map((f) => ({
      path: f.filename, previousPath: f.previous_filename ?? null, status: f.status,
      additions: f.additions, deletions: f.deletions, patch: f.patch ?? null,
    })),
    threads: pr.reviewThreads.nodes.map((t) => ({
      id: t.id, path: t.path, isOutdated: t.isOutdated, isResolved: t.isResolved, subjectType: t.subjectType, diffSide: t.diffSide,
      line: t.line, startLine: t.startLine, originalLine: t.originalLine,
      comments: t.comments.nodes.map((c) => ({
        id: c.id, author: c.author?.login ?? 'ghost', body: c.body, createdAt: c.createdAt, url: c.url, originalCommit: c.originalCommit?.oid ?? null,
        pending: c.state === 'PENDING',
      })),
      commentsTruncated: (t.comments.totalCount ?? 0) > t.comments.nodes.length,
    })),
    threadsTruncated: !!pr.reviewThreads.pageInfo?.hasNextPage || (pr.reviewThreads.totalCount ?? 0) > pr.reviewThreads.nodes.length,
  };
}

/** Base (at the merge base) and head contents of one file, normalized. Missing side → ''. */
export async function loadFileContents(gh: GitHub, repo: Repo, pr: PrDetails, file: PrFile): Promise<{ base: string; head: string }> {
  const R = `/repos/${encodeURIComponent(repo.owner)}/${encodeURIComponent(repo.name)}`;
  const enc = (p: string) => p.split('/').map(encodeURIComponent).join('/');
  const basePath = file.previousPath ?? file.path;
  const [base, head] = await Promise.all([
    file.status === 'added' ? Promise.resolve('') : gh.restRaw(`${R}/contents/${enc(basePath)}?ref=${pr.mergeBase}`).then((t) => t ?? ''),
    file.status === 'removed' ? Promise.resolve('') : gh.restRaw(`${R}/contents/${enc(file.path)}?ref=${pr.headSha}`).then((t) => t ?? ''),
  ]);
  return { base: normalizeText(base), head: normalizeText(head) };
}
